/**
 * QuoteSheetModal - Shared quote detail modal component
 *
 * Features:
 * - Uses QuotePdfDocument from @semo/shared-pdf-components for unified PDF/Preview
 * - Backend SSoT for blur state (isAnonymous)
 * - Complete attachment display and download
 * - Accessible: ARIA attributes, keyboard support
 * - Responsive: Mobile-first design
 */

import { useCallback, useState } from "react";
import {
  X,
  Download,
  FileText,
  Edit2,
  Loader2,
  RefreshCw,
  Lock,
} from "@semo/icons";
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@semo/shared-ui";
import { toast } from "sonner";
import { cn } from "@semo/shared-ui";
import { formatDateKorean, useDownloadFile } from "@semo/shared-utils";
import {
  QuotePdfDocument,
  mapQuoteMessageToQuotePdfData,
} from "@semo/shared-pdf-components";
import { downloadPdf, quotePdfPath } from "@semo/api-contracts";
import type {
  QuoteMessageResponseDto,
  QuoteMessageResponseDtoQuoteDataItemsItem,
  QuoteMessageResponseDtoQuoteDataAttachmentsItem,
} from "@semo/api-contracts";
import {
  PDF_INFO_MISSING_MESSAGE,
  pdfDownloadErrorMessage,
} from "../../utils/chat-error-messages";

/**
 * 계약 전(상대가 가려진 견적)에 내려받으려 할 때의 안내 — 사용자매뉴얼 R06 5.2.4.1 1번.
 * 누르면 토스트로, 마우스를 올리면 말풍선으로 같은 문구를 보인다.
 */
const CONTRACT_REQUIRED_FOR_DOWNLOAD_MESSAGE =
  "계약 완료 후 다운로드할 수 있습니다.";

// ✅ Type aliases extending OpenAPI types with UI-only 'id' field
type EditableQuoteItem = QuoteMessageResponseDtoQuoteDataItemsItem & {
  id: number;
};
type EditableQuoteAttachment =
  QuoteMessageResponseDtoQuoteDataAttachmentsItem & { id: string };

/** 견적 payload — 편집 화면으로 넘길 당사자 정보를 여기서 꺼낸다. */
type ChatQuoteData = NonNullable<QuoteMessageResponseDto["quoteData"]>;

/**
 * 세금 축 세 필드는 백엔드 DTO 에 방금 추가됐고, 생성 계약(`@semo/api-contracts`)은
 * `pnpm sync:api-types` 를 돌려야 따라온다. 그때까지 `Partial` 로 얹어 둔다 —
 * 캐스팅 없이 읽히고, 계약이 갱신되면 이 별칭만 지우면 된다.
 */
type ChatQuoteDataWithTax = ChatQuoteData &
  Partial<{
    taxType: "EXCLUSIVE" | "INCLUSIVE";
    supplyAmount: number;
    taxAmount: number;
  }>;

export interface QuoteSheetModalProps {
  /** Quote message data from API */
  message: QuoteMessageResponseDto;
  /** Close callback */
  onClose: () => void;
  /** Backend-determined blur state (based on isAnonymous) */
  isBlur?: boolean;
  /** User role - Vendor always sees their own info without blur */
  role?: "buyer" | "supplier";
  /** Allow editing of quote (typically only for vendor) */
  allowEdit?: boolean;
  /** Called when edit button is clicked - parent should handle showing EditQuoteModal */
  onEditClick?: (quoteData: {
    items: EditableQuoteItem[];
    attachments: EditableQuoteAttachment[];
    /**
     * 편집 화면이 견적서를 다시 그리려면 금액 축과 당사자 정보가 함께 가야 한다.
     * 종전에는 품목·첨부만 넘겨서, 편집 화면이 공급자 정보를 목업 리터럴로 채우고
     * 금액도 자체 규칙(`/1.1`)으로 다시 계산했다 — 그게 «미리보기와 견적서가 다르다» 였다.
     */
    taxType: "EXCLUSIVE" | "INCLUSIVE";
    supplier: NonNullable<ChatQuoteData["supplier"]> | undefined;
    buyerName: string | undefined;
  }) => void;
  /** Context for filename generation */
  fileNameContext?: {
    projectTitle?: string;
  };
}

export function QuoteSheetModal({
  message,
  onClose,
  isBlur = true,
  role = "buyer",
  allowEdit = false,
  onEditClick,
  fileNameContext,
}: QuoteSheetModalProps) {
  const quoteData: ChatQuoteDataWithTax | undefined = message.quoteData;
  // Vendor always sees their own info without blur
  const shouldBlurSupplier = role === "buyer" ? isBlur : false;

  // Early return if no data
  if (!quoteData) return null;

  // Map API data to QuotePdfData format
  const pdfData = mapQuoteMessageToQuotePdfData(
    {
      id: message.id,
      createdAt: message.createdAt,
      quoteData: quoteData as Parameters<
        typeof mapQuoteMessageToQuotePdfData
      >[0]["quoteData"],
    },
    {
      dateFormatter: formatDateKorean,
    },
  );

  // Extract data for header/footer functionality
  const isContracted = quoteData.isContracted;
  const date = formatDateKorean(message.createdAt);
  const attachments = quoteData.attachments || [];
  const pdfUrl = quoteData.pdfUrl;
  const versionNumber = quoteData.versionNumber;
  const versionId = (quoteData as any).quoteVersionId;

  // ✅ Allow download if:
  // 1. Anonymization is lifted (user can see supplier info, e.g., via subscription)
  // 2. Contract is already completed
  const canDownloadPdf = !shouldBlurSupplier || isContracted;

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  /**
   * Transform API quote items to EditQuoteModal format
   * Maps QuoteMessageResponseDtoQuoteDataItemsItem[] → EditableQuoteItem[]
   * Now using OpenAPI type aliases (unitPrice, amount, unit fields)
   */
  const transformToEditableItems = (): EditableQuoteItem[] => {
    if (!quoteData?.items || !Array.isArray(quoteData.items)) {
      return [];
    }

    return quoteData.items.map(
      (
        item: QuoteMessageResponseDtoQuoteDataItemsItem,
        index: number,
      ): EditableQuoteItem => ({
        ...item, // ✅ Spread OpenAPI fields (unitPrice, amount, unit, etc.)
        id: index + 1, // UI-only field for React keys
        name: item.name ?? "",
        spec: item.spec ?? "",
        quantity: item.quantity ?? 0,
        unit: item.unit ?? "개",
        unitPrice: item.unitPrice ?? 0, // ✅ Correct field name from OpenAPI
        amount: item.amount ?? 0, // ✅ Correct field name from OpenAPI
      }),
    );
  };

  /**
   * Transform API attachments to EditQuoteModal format
   * Maps QuoteMessageResponseDtoQuoteDataAttachmentsItem[] → EditableQuoteAttachment[]
   * Now using OpenAPI type aliases (fileName, fileSize fields)
   */
  const transformToEditableAttachments = (): EditableQuoteAttachment[] => {
    if (!quoteData?.attachments || !Array.isArray(quoteData.attachments)) {
      return [];
    }

    return quoteData.attachments.map(
      (
        attachment: QuoteMessageResponseDtoQuoteDataAttachmentsItem,
        index: number,
      ): EditableQuoteAttachment => ({
        ...attachment, // ✅ Spread OpenAPI fields (fileName, fileSize, etc.)
        id: attachment.url ?? `file-${index}`, // UI-only field (use URL or fallback index)
        fileName: attachment.fileName ?? "", // ✅ Correct field name from OpenAPI
        fileSize: attachment.fileSize ?? 0, // ✅ Correct field name from OpenAPI
      }),
    );
  };

  const editableItems = transformToEditableItems();
  const editableAttachments = transformToEditableAttachments();

  // ✅ Use common download hook
  const { downloadFile } = useDownloadFile();

  /**
   * Handle PDF download with Fallback generation
   * Always calls the backend API to ensure a valid Signed URL or trigger regeneration.
   */
  const handlePdfDownload = useCallback(async () => {
    if (!canDownloadPdf) {
      toast.warning(CONTRACT_REQUIRED_FOR_DOWNLOAD_MESSAGE);
      return;
    }

    // Clean up filename components
    const safeProjectTitle =
      fileNameContext?.projectTitle?.replace(/[\/\\:*?"<>|]/g, "") || "";
    const dateStr = date.replace(/\./g, "");

    let fileName = "";
    if (safeProjectTitle) {
      fileName = `[SEMO] ${safeProjectTitle}_견적서_v${versionNumber || 1}_${dateStr}.pdf`;
    } else {
      fileName = `견적서_v${versionNumber || 1}_${dateStr}.pdf`;
    }

    // 내려받을 견적 버전을 모르면 서버에 물을 것도 없다(매뉴얼 R06 5.2.4.1 2번).
    if (!versionId) {
      toast.error(PDF_INFO_MISSING_MESSAGE);
      return;
    }

    // 저장된 pdfUrl 은 비공개 버킷 주소라 링크로 걸지 않는다. 서버 프록시에서 받아온다.
    // 아직 PDF 가 없으면 프록시가 그 자리에서 만들어 준다.
    setIsGeneratingPdf(true);
    try {
      await downloadPdf(quotePdfPath(versionId as string), fileName);
    } catch (error) {
      console.error("PDF download/generation failed:", error);
      // 서버가 파일을 주지 못함 → 5.2.4.1 2번, 그 밖의 처리 오류 → 3번
      toast.error(pdfDownloadErrorMessage(error));
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [canDownloadPdf, versionNumber, date, fileNameContext, versionId]);

  return (
    <TooltipProvider>
      <div
        className={cn(
          "bg-white ",
          "w-full h-full sm:h-full sm:w-full",
          "flex flex-col overflow-hidden",
        )}
      >
        {/* Header Toolbar */}
        <div
          className={cn(
            "h-[56px] sm:h-[60px]",
            "border-b border-slate-200 ",
            "flex items-center justify-between",
            "px-3 sm:px-6",
            "bg-slate-50  shrink-0",
          )}
        >
          <h2
            id="quote-sheet-title"
            className="text-sm sm:text-base font-semibold text-slate-900 "
          >
            견적서 상세
          </h2>
          <div className="flex items-center gap-1 sm:gap-2">
            {allowEdit && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "h-8 sm:h-9 gap-1 sm:gap-2",
                    "bg-white ",
                    "text-xs sm:text-sm px-2 sm:px-3",
                  )}
                  onClick={() =>
                    onEditClick?.({
                      taxType: quoteData.taxType ?? "EXCLUSIVE",
                      supplier: quoteData.supplier,
                      buyerName: quoteData.buyer?.name ?? undefined,
                      items: editableItems,
                      attachments: editableAttachments,
                    })
                  }
                  aria-label="견적서 편집"
                >
                  <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">편집</span>
                </Button>
                <div className="w-px h-4 bg-slate-300  mx-0.5 sm:mx-1" />
              </>
            )}
            {canDownloadPdf && !isGeneratingPdf ? (
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-8 sm:h-9 gap-1 sm:gap-2",
                  "bg-white",
                  "text-xs sm:text-sm px-2 sm:px-3",
                )}
                onClick={handlePdfDownload}
                aria-label="견적서 PDF 다운로드"
              >
                {pdfUrl ? (
                  <>
                    <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">다운로드</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">
                      PDF 생성 및 다운로드
                    </span>
                  </>
                )}
              </Button>
            ) : (
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  {/* Visual replica of the disabled Button to avoid Radix UI Tooltip event blocking.
                      Removed tabIndex={0} to prevent Dialog from auto-focusing it and opening the tooltip immediately. */}
                  {/* 계약 전에 누르면 내려받지 않고 안내만 띄운다(매뉴얼 R06 5.2.3 10번·5.2.4.1 1번). */}
                  <div
                    className={cn(
                      "inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium border",
                      "h-8 sm:h-9 gap-1 sm:gap-2 px-2 sm:px-3 text-xs sm:text-sm",
                      "bg-white text-slate-900 border-slate-200",
                      "opacity-50 grayscale cursor-not-allowed",
                    )}
                    role="button"
                    aria-disabled="true"
                    aria-label="견적서 PDF 다운로드 (비활성화)"
                    onClick={
                      isGeneratingPdf
                        ? undefined
                        : () => {
                            void handlePdfDownload();
                          }
                    }
                  >
                    {isGeneratingPdf ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
                        <span className="hidden sm:inline">생성 중...</span>
                      </>
                    ) : (
                      <>
                        <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">다운로드</span>
                      </>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  className="bg-slate-900 text-white border-slate-800 z-[300]"
                >
                  <p className="flex items-center gap-1 font-medium text-xs">
                    <Lock className="w-3 h-3" />
                    {isGeneratingPdf
                      ? "PDF를 생성하고 있습니다. 잠시만 기다려주세요."
                      : role === "supplier"
                        ? "개인정보 보호를 위해 계약 체결 시 공개됩니다"
                        : CONTRACT_REQUIRED_FOR_DOWNLOAD_MESSAGE}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
            <div className="w-px h-4 bg-slate-300  mx-0.5 sm:mx-1" />
            <button
              onClick={onClose}
              className={cn(
                "p-1.5 sm:p-2",
                "hover:bg-slate-200 :bg-slate-700",
                "rounded-lg transition-colors",
                "focus:outline-none focus:ring-2 focus:ring-primary",
              )}
              aria-label="견적서 닫기"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5 text-slate-500 " />
            </button>
          </div>
        </div>

        {/* Document Content - Using Shared QuotePdfDocument */}
        <div className="flex-1 overflow-y-auto bg-[#f2f5f9]  p-2 sm:p-4 md:p-8 custom-scrollbar">
          <div className="mx-auto max-w-[210mm] w-full">
            {/* QuotePdfDocument for unified PDF/Preview rendering */}
            <QuotePdfDocument
              data={pdfData}
              isPdf={false}
              isBlur={shouldBlurSupplier}
            />

            {/* Attachments Section - Frontend Only */}
            {attachments.length > 0 && (
              <div className="mt-6 bg-white border border-[#e8edf3] rounded-lg p-4 sm:p-6">
                <div className="font-semibold text-gray-800 mb-3 text-sm sm:text-sm flex items-center gap-2">
                  <span className="w-1 h-4 bg-primary rounded-sm block" />
                  첨부파일 ({attachments.length})
                </div>
                <div className="space-y-2">
                  {attachments.map((attachment, index) => (
                    <div
                      key={`${attachment.url}-${index}`}
                      className="flex items-center gap-3 p-3 bg-[#f9fafb] border border-[#eef2f7] rounded-md hover:border-[#dbe8f7] hover:shadow-sm transition-all group"
                    >
                      {/* File Icon */}
                      <div className="w-8 h-8 rounded-md bg-primary/5 border border-[#dbe8f7] flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 text-primary-text" />
                      </div>

                      {/* File Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {attachment.fileName}
                        </p>
                        {attachment.fileSize && (
                          <p className="text-2xs text-gray-500">
                            {(attachment.fileSize / 1024).toFixed(1)} KB
                          </p>
                        )}
                      </div>

                      {/* Download Button */}
                      <button
                        type="button"
                        onClick={() =>
                          downloadFile(
                            attachment.url || "",
                            attachment.fileName || "file",
                          )
                        }
                        className={cn(
                          "w-8 h-8 rounded-md",
                          "bg-white border border-[#eef2f7]",
                          "flex items-center justify-center flex-shrink-0",
                          "text-gray-500 hover:text-primary-text",
                          "hover:border-primary hover:bg-primary/5",
                          "transition-all opacity-0 group-hover:opacity-100",
                        )}
                        title="파일 다운로드"
                        aria-label={`${attachment.fileName} 다운로드`}
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
